import type { TemplateRegistration, TemplateData } from './types';
import { sanitizeText, sanitizeSeoText, escapeAttr } from './sanitize';
import { getIconSvg, renderStars, buildFontsUrl, buildNavLinks, NavDataFlags, truncateAtSentence } from './helpers';

export const jungleTrek: TemplateRegistration = {
  meta: {
    id: 'jungle-trek',
    name: 'Jungle Trek',
    category: 'Creative',
    description: 'Lush green with layered overlapping sections',
    previewImage: 'https://images.unsplash.com/photo-1448375240586-882707db888b?w=800&q=80',
    supportedFeatures: ['contact-form', 'phone-display', 'address-map', 'business-hours', 'about-section', 'services-list', 'testimonials', 'google-reviews', 'social-links', 'faq', 'price-list', 'promotions', 'photo-gallery'],
    defaultEnabledFeatures: ['contact-form', 'phone-display', 'about-section', 'services-list', 'testimonials'],
  },
  render(data: TemplateData, enabledFeatures: Set<string>): string {
    const safeName = sanitizeText(data.businessName);
    const safeHeadline = sanitizeText(data.headline);
    const safeAbout = sanitizeText(data.aboutText);
    const safeSeoTitle = sanitizeSeoText(data.seo.title, 70);
    const safeSeoDesc = sanitizeSeoText(data.seo.description, 160);

    const phone = data.businessData?.phone || null;
    const contactEmail = data.businessData?.contactEmail || null;
    const rating = data.businessData?.rating || null;
    const reviewCount = data.businessData?.reviewCount || 0;
    const businessHours = data.businessData?.businessHours || [];
    const photos = data.businessData?.photos || [];
    const address = data.businessData?.address || '';
    const googleMapsUrl = data.businessData?.googleMapsUrl || null;
    const galleryImages = photos.slice(1, 6);

    const navDataFlags: NavDataFlags = {
      hasServices: data.services.length > 0,
      hasTestimonials: data.testimonials.length > 0,
      hasGalleryPhotos: galleryImages.length > 0,
      hasFaq: !!(data.faq && data.faq.length > 0),
      hasPriceList: !!(data.priceList && data.priceList.length > 0),
    };
    const navLinks = buildNavLinks(enabledFeatures, navDataFlags);
    const fontsUrl = buildFontsUrl(['Signika', 'Cabin']);

    const sections: string[] = [];

    // --- Google Reviews ---
    if (enabledFeatures.has('google-reviews') && rating) {
      sections.push(`
      <section class="google-reviews">
        <div class="container">
          <div class="review-badge">
            <div class="review-stars">${renderStars(Math.round(rating))}</div>
            <span class="review-text">${rating.toFixed(1)} out of 5 &mdash; ${reviewCount} Google reviews</span>
          </div>
        </div>
      </section>`);
    }

    // --- Promotions ---
    if (enabledFeatures.has('promotions') && data.promotion) {
      const promoTitle = sanitizeText(data.promotion.title);
      const promoDesc = sanitizeText(data.promotion.description);
      const promoDiscount = sanitizeText(data.promotion.discount);
      sections.push(`
      <section class="promo-banner">
        <div class="container">
          <div class="promo-inner">
            <span class="promo-badge">${promoDiscount}</span>
            <div class="promo-content">
              <strong>${promoTitle}</strong>
              <span>${promoDesc}</span>
            </div>
          </div>
        </div>
      </section>`);
    }

    // --- About ---
    if (enabledFeatures.has('about-section')) {
      sections.push(`
      <section id="about" class="about-fullbleed">
        <div class="container">
          <div class="about-overlay-panel">
            <h2 class="section-title" style="color: var(--text);">About Us</h2>
            <p class="about-text">${safeAbout}</p>
          </div>
        </div>
      </section>`);
    }

    // --- Services ---
    if (enabledFeatures.has('services-list') && data.services.length > 0) {
      const serviceCards = data.services.map((s) => {
        const sName = sanitizeText(s.name);
        const sDesc = sanitizeText(s.description);
        return `
          <div class="service-card">
            <div class="service-icon">${getIconSvg(s.icon || 'star')}</div>
            <h3>${sName}</h3>
            <p>${sDesc}</p>
            ${s.price ? `<span class="service-price">${sanitizeText(s.price)}</span>` : ''}
          </div>`;
      }).join('');
      sections.push(`
      <section id="services" class="section section-overlap section-alt">
        <div class="container">
          <h2 class="section-title">Our Services</h2>
          <div class="services-grid">${serviceCards}</div>
        </div>
      </section>`);
    }

    // --- Photo Gallery ---
    if (enabledFeatures.has('photo-gallery') && galleryImages.length > 0) {
      const imgs = galleryImages.map((url) => {
        return `<div class="gallery-item"><img src="${escapeAttr(url)}" alt="${safeName}" loading="lazy"></div>`;
      }).join('');
      sections.push(`
      <section id="gallery" class="section section-overlap section-light">
        <div class="container">
          <h2 class="section-title">Gallery</h2>
          <div class="gallery-grid">${imgs}</div>
        </div>
      </section>`);
    }

    // --- Testimonials ---
    if (enabledFeatures.has('testimonials') && data.testimonials.length > 0) {
      const cards = data.testimonials.map((t) => {
        const tName = sanitizeText(t.name);
        const tContent = sanitizeText(t.content);
        const tRole = t.role ? sanitizeText(t.role) : '';
        return `
          <div class="testimonial-card">
            <div class="testimonial-body">
              <p>&ldquo;${tContent}&rdquo;</p>
            </div>
            <div class="testimonial-author">
              <strong>${tName}</strong>
              ${tRole ? `<span>${tRole}</span>` : ''}
              ${t.rating ? `<div class="testimonial-stars">${renderStars(t.rating)}</div>` : ''}
            </div>
          </div>`;
      }).join('');
      sections.push(`
      <section id="testimonials" class="section section-overlap section-alt">
        <div class="container">
          <h2 class="section-title">What People Say</h2>
          <div class="testimonials-grid">${cards}</div>
        </div>
      </section>`);
    }

    // --- FAQ ---
    if (enabledFeatures.has('faq') && data.faq && data.faq.length > 0) {
      const items = data.faq.map(f => {
        const q = sanitizeText(f.question);
        const a = sanitizeText(f.answer);
        return `
          <div class="faq-item">
            <h3 class="faq-q">${q}</h3>
            <p class="faq-a">${a}</p>
          </div>`;
      }).join('');
      sections.push(`
      <section id="faq" class="section section-overlap section-light">
        <div class="container">
          <h2 class="section-title">FAQ</h2>
          <div class="faq-list">${items}</div>
        </div>
      </section>`);
    }

    // --- Price List ---
    if (enabledFeatures.has('price-list') && data.priceList && data.priceList.length > 0) {
      const rows = data.priceList.map(p => {
        const pName = sanitizeText(p.name);
        const pDesc = sanitizeText(p.description);
        const pPrice = sanitizeText(p.price);
        return `
          <div class="price-row">
            <div class="price-info">
              <h3>${pName}</h3>
              <p>${pDesc}</p>
            </div>
            <span class="price-amount">${pPrice}</span>
          </div>`;
      }).join('');
      sections.push(`
      <section id="pricing" class="section section-overlap section-alt">
        <div class="container">
          <h2 class="section-title">Pricing</h2>
          <div class="price-list">${rows}</div>
        </div>
      </section>`);
    }

    // --- Business Hours ---
    if (enabledFeatures.has('business-hours') && businessHours.length > 0) {
      const hoursList = businessHours.map(h => `<li>${sanitizeText(h)}</li>`).join('');
      sections.push(`
      <section class="section section-overlap section-light">
        <div class="container">
          <h2 class="section-title">Hours</h2>
          <ul class="hours-list">${hoursList}</ul>
        </div>
      </section>`);
    }

    // --- Contact ---
    if (enabledFeatures.has('contact-form')) {
      sections.push(`
      <section id="contact" class="section section-overlap section-alt">
        <div class="container">
          <h2 class="section-title">Get In Touch</h2>
          <div class="contact-grid">
            <div class="contact-info">
              ${enabledFeatures.has('phone-display') && phone ? `<div class="contact-item">${getIconSvg('phone')}<a href="tel:${escapeAttr(phone)}">${sanitizeText(phone)}</a></div>` : ''}
              ${enabledFeatures.has('address-map') && address ? `<div class="contact-item">${getIconSvg('map-pin')}<span>${sanitizeText(address)}</span></div>` : ''}
              ${enabledFeatures.has('address-map') && googleMapsUrl ? `<a class="map-link" href="${escapeAttr(googleMapsUrl)}" target="_blank" rel="noopener noreferrer">View on Google Maps</a>` : ''}
            </div>
            <form class="contact-form" ${contactEmail ? `action="https://formsubmit.co/${escapeAttr(contactEmail)}" method="POST"` : 'onsubmit="return false;"'}>
              <input type="text" placeholder="Your Name" required>
              <input type="email" placeholder="Your Email" required>
              <textarea placeholder="Message" rows="4" required></textarea>
              <button type="submit">Send Message</button>
            </form>
          </div>
        </div>
      </section>`);
    }

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${safeSeoTitle}</title>
  <meta name="description" content="${safeSeoDesc}">
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="${fontsUrl}" rel="stylesheet">
  <style>
    *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
    :root {
      --primary: #166534; --bg: #F0FDF4; --bg-alt: #DCFCE7; --accent: #EA580C;
      --text: #14532D; --text-muted: #4D7C5C;
      --font-heading: 'Signika', sans-serif; --font-body: 'Cabin', sans-serif;
      --shadow-sm: 0 2px 6px rgba(22,101,52,.08); --shadow-md: 0 6px 20px rgba(22,101,52,.12);
    }
    html { scroll-behavior: smooth; }
    body { font-family: var(--font-body); background: var(--bg); color: var(--text); line-height: 1.6; }
    .container { max-width: 1200px; margin: 0 auto; padding: 0 24px; }
    a { color: var(--primary); text-decoration: none; transition: opacity .2s; }
    a:hover { opacity: .8; }

    /* NAV */
    .navbar { position: fixed; top: 0; left: 0; right: 0; z-index: 100; background: rgba(240,253,244,.95); backdrop-filter: blur(10px); border-bottom: 1px solid #BBF7D0; box-shadow: var(--shadow-sm); }
    .nav-inner { display: flex; align-items: center; justify-content: space-between; padding: 14px 24px; max-width: 1200px; margin: 0 auto; }
    .nav-brand { font-family: var(--font-heading); font-size: 1.4rem; color: var(--primary); font-weight: 700; }
    .nav-links { display: flex; gap: 24px; list-style: none; }
    .nav-links a { color: var(--text-muted); font-size: .85rem; text-transform: uppercase; letter-spacing: 1px; font-weight: 600; }
    .nav-links a:hover { color: var(--accent); }
    .nav-toggle { display: none; background: none; border: none; cursor: pointer; }
    .nav-toggle span { display: block; width: 26px; height: 3px; background: var(--primary); margin: 5px 0; transition: .3s; border-radius: 2px; }

    /* HERO */
    .hero { position: relative; min-height: 100vh; display: flex; align-items: center; overflow: hidden; background: var(--primary); }
    .hero-layer { position: absolute; inset: 0; }
    .hero-layer-1 { background: linear-gradient(135deg, rgba(22,101,52,1) 0%, rgba(20,83,45,0.9) 100%); z-index: 1; }
    .hero-layer-2 { background: radial-gradient(ellipse at 30% 50%, rgba(234,88,12,.15) 0%, transparent 60%); z-index: 2; }
    .hero-layer-3 { background: radial-gradient(ellipse at 70% 30%, rgba(187,247,208,.1) 0%, transparent 50%); z-index: 3; }
    .hero-layer-4 { background: linear-gradient(180deg, transparent 60%, rgba(20,83,45,.4) 100%); z-index: 4; }
    .hero-content { position: relative; z-index: 5; padding: 120px 24px 80px; max-width: 1200px; margin: 0 auto; width: 100%; }
    .hero h1 { font-family: var(--font-heading); font-size: clamp(2.8rem, 7vw, 5rem); line-height: 1.1; margin-bottom: 16px; font-weight: 700; color: #fff; }
    .hero h1 .accent { color: var(--accent); }
    .hero-sub { font-size: clamp(1rem, 2vw, 1.15rem); color: rgba(255,255,255,.8); max-width: 560px; margin-bottom: 32px; }
    .hero-cta { display: inline-block; padding: 14px 36px; background: var(--accent); color: #fff; font-family: var(--font-heading); font-size: 1rem; font-weight: 700; text-transform: uppercase; letter-spacing: 1.5px; border: none; border-radius: 8px; cursor: pointer; transition: transform .2s, box-shadow .2s; box-shadow: 0 4px 16px rgba(234,88,12,.3); }
    .hero-cta:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(234,88,12,.4); opacity: 1; color: #fff; }

    /* SECTIONS - OVERLAP */
    .section { padding: 80px 0; position: relative; z-index: 1; }
    .section-overlap { margin-top: -40px; padding-top: 120px; border-radius: 24px 24px 0 0; }
    .section-light { background: var(--bg); }
    .section-alt { background: var(--bg-alt); }
    .section-title { font-family: var(--font-heading); font-size: clamp(1.8rem, 4vw, 2.6rem); font-weight: 700; margin-bottom: 40px; color: var(--text); border-left: 5px solid var(--primary); padding-left: 16px; }

    /* ABOUT */
    .about-fullbleed { background: var(--primary); padding: 80px 0; position: relative; }
    .about-overlay-panel { background: rgba(240,253,244,.95); max-width: 800px; margin: 0 auto; padding: 48px 40px; border-radius: 12px; box-shadow: var(--shadow-md); }
    .about-text { font-size: 1.05rem; color: var(--text-muted); line-height: 1.9; }

    /* SERVICES */
    .services-grid { columns: 2; column-gap: 20px; }
    .service-card { break-inside: avoid; margin-bottom: 20px; padding: 32px 24px; background: #fff; border-radius: 12px; border-left: 5px solid var(--primary); box-shadow: var(--shadow-sm); transition: transform .3s, box-shadow .3s; }
    .service-card:hover { transform: translateY(-4px); box-shadow: var(--shadow-md); }
    .service-icon { margin-bottom: 12px; }
    .icon-char { font-size: 28px; line-height: 1; color: var(--accent); }
    .service-card h3 { font-family: var(--font-heading); font-size: 1.15rem; font-weight: 700; margin-bottom: 8px; color: var(--text); }
    .service-card p { color: var(--text-muted); font-size: .9rem; line-height: 1.6; }
    .service-price { display: inline-block; margin-top: 12px; font-weight: 700; font-size: 1.05rem; color: var(--accent); }

    /* GALLERY */
    .gallery-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
    .gallery-item img { width: 100%; height: 220px; object-fit: cover; display: block; border-radius: 12px; border: 3px solid var(--primary); box-shadow: var(--shadow-sm); transition: transform .3s; }
    .gallery-item img:hover { transform: scale(1.03); }

    /* TESTIMONIALS */
    .testimonials-grid { display: flex; gap: 20px; overflow-x: auto; scroll-snap-type: x mandatory; -webkit-overflow-scrolling: touch; padding-bottom: 12px; }
    .testimonial-card { min-width: 320px; flex-shrink: 0; scroll-snap-align: start; padding: 28px; background: #fff; border-radius: 12px; box-shadow: var(--shadow-sm); border-left: 5px solid var(--accent); transition: transform .3s; }
    .testimonial-card:hover { transform: translateY(-4px); box-shadow: var(--shadow-md); }
    .testimonial-body p { font-size: 1rem; font-style: italic; color: var(--text-muted); line-height: 1.7; margin-bottom: 16px; }
    .testimonial-author strong { display: block; color: var(--text); font-size: 1rem; }
    .testimonial-author span { color: var(--text-muted); font-size: .85rem; }
    .testimonial-stars { margin-top: 8px; }
    .star { color: #D6D3D1; font-size: 1rem; }
    .star.filled { color: var(--accent); }

    /* FAQ */
    .faq-list { max-width: 800px; }
    .faq-item { border-bottom: 1px solid #BBF7D0; padding: 24px 0; }
    .faq-q { font-family: var(--font-heading); font-size: 1.1rem; font-weight: 700; margin-bottom: 10px; color: var(--text); }
    .faq-a { color: var(--text-muted); line-height: 1.7; }

    /* PRICE LIST */
    .price-list { max-width: 800px; }
    .price-row { display: flex; justify-content: space-between; align-items: flex-start; padding: 24px 0; border-bottom: 1px solid #BBF7D0; gap: 24px; }
    .price-info h3 { font-family: var(--font-heading); font-size: 1.1rem; font-weight: 700; margin-bottom: 4px; color: var(--text); }
    .price-info p { color: var(--text-muted); font-size: .9rem; }
    .price-amount { font-family: var(--font-heading); font-size: 1.4rem; color: var(--accent); white-space: nowrap; font-weight: 700; }

    /* HOURS */
    .hours-list { list-style: none; max-width: 400px; }
    .hours-list li { padding: 12px 0; border-bottom: 1px solid #BBF7D0; color: var(--text-muted); font-size: .95rem; }

    /* GOOGLE REVIEWS */
    .google-reviews { background: var(--primary); padding: 16px 0; position: relative; z-index: 2; }
    .review-badge { display: flex; align-items: center; justify-content: center; gap: 12px; flex-wrap: wrap; }
    .review-badge .star, .review-badge .star.filled { color: #fff; font-size: 1.2rem; }
    .review-text { color: #fff; font-weight: 700; font-size: .95rem; }

    /* PROMO */
    .promo-banner { background: #fff; padding: 20px 0; border-bottom: 1px solid #BBF7D0; position: relative; z-index: 2; }
    .promo-inner { display: flex; align-items: center; gap: 20px; flex-wrap: wrap; }
    .promo-badge { background: var(--accent); color: #fff; font-family: var(--font-heading); padding: 8px 20px; font-size: 1rem; font-weight: 700; border-radius: 6px; }
    .promo-content strong { display: block; margin-bottom: 2px; color: var(--text); }
    .promo-content span { color: var(--text-muted); font-size: .9rem; }

    /* CONTACT */
    .contact-grid { display: flex; flex-direction: column; gap: 32px; max-width: 600px; margin: 0 auto; }
    .contact-info { display: flex; flex-direction: column; gap: 20px; }
    .contact-item { display: flex; align-items: center; gap: 12px; color: var(--text-muted); }
    .contact-item .icon-char { color: var(--primary); flex-shrink: 0; font-size: 20px; line-height: 1; }
    .contact-item a { color: var(--text); }
    .map-link { display: inline-block; margin-top: 8px; font-weight: 600; text-transform: uppercase; font-size: .85rem; letter-spacing: 1px; color: var(--accent); }
    .contact-form { display: flex; flex-direction: column; gap: 16px; }
    .contact-form input, .contact-form textarea { background: #fff; border: 1px solid #BBF7D0; border-radius: 8px; color: var(--text); padding: 14px 16px; font-family: var(--font-body); font-size: .95rem; outline: none; transition: border-color .2s; }
    .contact-form input:focus, .contact-form textarea:focus { border-color: var(--primary); }
    .contact-form button { padding: 14px; background: var(--primary); color: #fff; font-family: var(--font-heading); font-size: 1rem; font-weight: 700; text-transform: uppercase; letter-spacing: 1.5px; border: none; border-radius: 8px; cursor: pointer; transition: transform .2s, box-shadow .2s; }
    .contact-form button:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(22,101,52,.25); }

    /* FOOTER */
    .footer { background: #14532D; padding: 40px 0; text-align: center; }
    .footer p { color: #86EFAC; font-size: .85rem; }

    /* RESPONSIVE */
    @media (max-width: 768px) {
      .nav-links { display: none; flex-direction: column; position: absolute; top: 100%; left: 0; right: 0; background: rgba(240,253,244,.98); border-bottom: 1px solid #BBF7D0; padding: 16px 24px; gap: 16px; }
      .nav-links.show { display: flex; }
      .nav-toggle { display: block; }
      .services-grid { columns: 1; }
      .gallery-grid { grid-template-columns: repeat(2, 1fr); }
      .testimonials-grid { flex-direction: column; }
      .testimonial-card { min-width: auto; }
      .price-row { flex-direction: column; gap: 8px; }
      .section-overlap { margin-top: -20px; padding-top: 100px; }
    }
    @media (max-width: 480px) {
      .gallery-grid { grid-template-columns: 1fr; }
      .hero-content { padding: 100px 16px 60px; }
      .section-overlap { margin-top: -10px; padding-top: 90px; }
    }
  </style>
</head>
<body>
  <nav class="navbar">
    <div class="nav-inner">
      <a href="#" class="nav-brand">${safeName}</a>
      <button class="nav-toggle" aria-label="Toggle menu" onclick="this.parentElement.querySelector('.nav-links').classList.toggle('show')">
        <span></span><span></span><span></span>
      </button>
      <ul class="nav-links">
        ${navLinks.map(l => `<li><a href="${l.href}">${l.label}</a></li>`).join('')}
      </ul>
    </div>
  </nav>

  <section class="hero">
    <div class="hero-layer hero-layer-1"></div>
    <div class="hero-layer hero-layer-2"></div>
    <div class="hero-layer hero-layer-3"></div>
    <div class="hero-layer hero-layer-4"></div>
    <div class="hero-content">
      <h1>${safeHeadline}</h1>
      <p class="hero-sub">${truncateAtSentence(safeAbout)}</p>
      ${enabledFeatures.has('contact-form') ? `<a href="#contact" class="hero-cta">Get Started</a>` : ''}
    </div>
  </section>

  ${sections.join('\n')}

  <footer class="footer">
    <div class="container">
      <p>${safeName}</p>
    </div>
  </footer>
</body>
</html>`;
  }
};
